import React, { useState, useEffect } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AdminMessageNotification = () => {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotification, setShowNotification] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);

  useEffect(() => {
    // Check for unread messages
    const checkUnread = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        // Use relative URL - works on any domain
        const response = await fetch('/api/messages/unread-count', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const newCount = data.unread_count;
          
          // Show notification if unread count increased
          if (newCount > unreadCount && newCount > 0) {
            setShowNotification(true);
            // Auto-hide notification after 5 seconds
            setTimeout(() => {
              setShowNotification(false);
            }, 5000);
          }
          
          setUnreadCount(newCount);
        }
      } catch (error) {
        console.error('Error checking unread messages:', error);
      }
    };

    // Check immediately
    checkUnread();
    
    // Check every 10 seconds
    const interval = setInterval(checkUnread, 10000);
    
    return () => clearInterval(interval);
  }, [unreadCount]);

  const handleClick = () => {
    setShowNotification(false);
    navigate('/live-chat');
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    setShowNotification(false);
  };

  if (unreadCount === 0) return null;

  return (
    <>
      {/* Notification Badge - Always visible when there are unread messages */}
      <div 
        onClick={handleClick}
        className="fixed top-4 right-4 z-50 cursor-pointer group"
      >
        <div className="relative bg-gradient-to-br from-purple-500 to-purple-600 hover:from-purple-400 hover:to-purple-500 p-3 rounded-full shadow-2xl shadow-purple-500/50 transition-all duration-300 hover:scale-110">
          <MessageCircle className="w-6 h-6 text-white" />
          <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full border-2 border-white flex items-center justify-center">
            <span className="text-white text-xs font-bold">{unreadCount > 9 ? '9+' : unreadCount}</span>
          </span>
        </div>
      </div>

      {/* Expandable Notification - Shows when new message arrives */}
      {showNotification && (
        <div 
          onClick={handleClick}
          className="fixed top-20 right-4 z-50 bg-gradient-to-br from-slate-800 to-slate-900 border border-cyan-500/30 rounded-lg shadow-2xl shadow-cyan-500/20 p-4 max-w-sm cursor-pointer hover:border-cyan-400/50 transition-all duration-300 animate-slide-in"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg flex-shrink-0">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-cyan-400 font-bold text-sm mb-1">New Message from Support</h3>
                <p className="text-cyan-600 text-xs">You have {unreadCount} unread message{unreadCount !== 1 ? 's' : ''} from the support team</p>
                <p className="text-cyan-500 text-xs mt-2 font-semibold">Click to view messages →</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-cyan-600 hover:text-cyan-400 transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default AdminMessageNotification;
